import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { toastErrors } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';
import { debug } from '@/shared/lib';
import { microphoneController } from '@/shared/lib/microphone/microphoneController';
import { toastCaller } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import {
  getTranscriptorErrorMessage,
  TRANSCRIPTOR_LANGUAGE,
  trackRecordingEvent,
  transcriptorModel,
} from '@/entities/transcriptor';

import { audioRecordingService } from '@/features/audioRecording';

import { transcriptorRecordingMessages } from '../config/i18n';
import { MicrophoneDevice } from '../config/types';

const { setConsultationData, startRecording: startRecordingAction } =
  transcriptorModel.actions;
const { startRecordingThunk, fetchConsultations } = transcriptorModel.thunks;

type UseStartRecordingParams = {
  patientID: string | undefined;
  schemaId: string | undefined;
  selectedOnDemandSeriesIds?: string[];
  language: TRANSCRIPTOR_LANGUAGE;
  selectedDevice: string;
  devices: MicrophoneDevice[];
  encounterId?: string;
  closeModalId?: ModalID;
  onCleanup?: () => void;
};

type UseStartRecordingReturn = {
  startRecording: () => Promise<void>;
  isStarting: boolean;
  error: string | null;
};

export const useStartRecording = ({
  patientID,
  schemaId,
  selectedOnDemandSeriesIds,
  language,
  selectedDevice,
  devices,
  encounterId,
  closeModalId = ModalID.TranscriptorRecording,
  onCleanup,
}: UseStartRecordingParams): UseStartRecordingReturn => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const startRecording = useCallback(async () => {
    if (onCleanup) {
      onCleanup();
    }

    // Get microphone name
    const microphoneName =
      devices.find((device) => device.deviceId === selectedDevice)?.label ||
      formatMessage(transcriptorRecordingMessages.defaultMicrophone);

    const result = await dispatch(
      startRecordingThunk({
        schemaId,
        selectedOnDemandSeriesIds,
        language,
        encounterId,
        patientId: patientID,
      }),
    )
      .unwrap()
      .catch((error: unknown) => {
        const refusal = getTranscriptorErrorMessage(error);

        toastCaller({
          type: 'error',
          heading: formatMessage(toastErrors.errorHeading),
          message: refusal
            ? formatMessage(refusal)
            : formatMessage(toastErrors.defaultError),
        });

        return null;
      });

    if (!result) {
      return;
    }

    try {
      dispatch(
        setConsultationData({
          consultationId: result.consultationId,
          fragmentId: result.fragmentId,
        }),
      );

      const stream =
        microphoneController.getStream() ||
        (await microphoneController.open(
          selectedDevice ? { deviceId: selectedDevice } : {},
        ));

      const recordingContext = {
        encounter_id: result.consultationId,
        fragment_id: result.fragmentId,
      };

      audioRecordingService.setCallbacks({
        onUploadIssue: (hasIssue) => {
          debug('Audio upload issue:', hasIssue);
        },
        onError: (error) => {
          debug('Audio recording error:', error);
          trackRecordingEvent('upload_failed', { ...recordingContext, error });
          dispatch(transcriptorModel.actions.stopRecording());
        },
        onStopped: (stopReason) => {
          debug('Recording stopped and all chunks uploaded');
          trackRecordingEvent('stopped', {
            ...recordingContext,
            stop_reason: stopReason,
          });
          dispatch(transcriptorModel.actions.stopRecording());

          if (encounterId) {
            // Skip the list refresh because its stale status would overwrite the optimistic processing state.
            dispatch(
              transcriptorModel.actions.setConsultationStatus({
                consultationId: encounterId,
                status: 'processing',
              }),
            );
            return;
          }

          if (patientID) {
            dispatch(fetchConsultations({ patientId: patientID }));
          }
        },
        onAudioIssue: (issue) => {
          debug('Audio issue state changed:', issue);
          trackRecordingEvent('audio_issue', {
            ...recordingContext,
            reason: issue ?? 'cleared',
          });
          dispatch(transcriptorModel.actions.setAudioIssue(issue));
        },
        onChunkUploaded: (uploadedAt, chunkIndex) => {
          trackRecordingEvent('chunk_uploaded', {
            ...recordingContext,
            chunk_index: chunkIndex,
          });
          dispatch(transcriptorModel.actions.updateLastSavedAt(uploadedAt));
        },
        onDiagnostic: ({ name, attributes }) => {
          trackRecordingEvent(name, { ...recordingContext, ...attributes });
        },
      });

      await audioRecordingService.initialize(stream);
      await audioRecordingService.startRecording(
        result.consultationId,
        result.fragmentId,
      );
      trackRecordingEvent('started', {
        ...recordingContext,
        microphone: microphoneName,
      });

      dispatch(
        startRecordingAction({
          microphoneName:
            process.env.NODE_ENV === 'development' && !selectedDevice
              ? formatMessage(transcriptorRecordingMessages.testMicrophone)
              : microphoneName,
          consultationType: schemaId ?? '',
          language,
          patientId: patientID || '',
        }),
      );

      if (patientID && !encounterId) {
        dispatch(fetchConsultations({ patientId: patientID }));
      }
    } catch (error) {
      debug('Failed to start recording after consultation created:', error);
    } finally {
      dispatch(modalModel.actions.closeModal(closeModalId));
    }
  }, [
    onCleanup,
    devices,
    selectedDevice,
    dispatch,
    schemaId,
    selectedOnDemandSeriesIds,
    language,
    patientID,
    encounterId,
    closeModalId,
    formatMessage,
  ]);

  return {
    startRecording,
    isStarting: false,
    error: null,
  };
};
